'use client'

import { useEffect, useRef } from 'react'

// Accessible focus trap for modal dialogs / bottom sheets.
// Usage:
//   const ref = useFocusTrap({ active: isOpen, onEscape: () => setOpen(false) })
//   return <div ref={ref} role="dialog" aria-modal="true">...</div>
//
// Behavior when active === true:
//   - Focuses the first focusable child on mount
//   - Intercepts Tab / Shift+Tab to cycle within the container
//   - Intercepts Escape and calls onEscape()
//   - Restores focus to the previously focused element on unmount / active=false
//
// When active === false, returns a ref but does nothing.
export default function useFocusTrap({ active, onEscape }) {
  const containerRef = useRef(null)

  useEffect(() => {
    if (!active) return

    const container = containerRef.current
    if (!container) return

    const previouslyFocused = document.activeElement

    const getFocusables = () =>
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )

    const focusables = getFocusables()
    if (focusables.length > 0) {
      focusables[0].focus()
    }

    function handleKeyDown(e) {
      if (e.key === 'Escape' && onEscape) {
        e.stopPropagation()
        e.preventDefault()
        onEscape()
        return
      }
      if (e.key !== 'Tab') return

      // Re-query each keypress (dialog content may change)
      const current = getFocusables()
      if (current.length === 0) return

      const first = current[0]
      const last = current[current.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first || !container.contains(document.activeElement)) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last || !container.contains(document.activeElement)) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    container.addEventListener('keydown', handleKeyDown)

    return () => {
      container.removeEventListener('keydown', handleKeyDown)
      if (previouslyFocused instanceof HTMLElement) {
        // Defer to next tick to avoid losing focus before any re-render
        setTimeout(() => {
          previouslyFocused.focus?.()
        }, 0)
      }
    }
  }, [active, onEscape])

  return containerRef
}
